import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Default flags - all disabled by default for safety
const DEFAULT_FLAGS = {
  community_enabled: false,
  live_chat_enabled: false,
  media_uploads_enabled: false,
  work_tracking_enabled: false,
  referrals_enabled: false,
  payments_enabled: false,
  languages_enabled: false,
  ai_content_drafting_enabled: false,
  ai_content_images_enabled: false,
  publish_readiness_enabled: false,
  pillar_byoc_enabled: false,
  ai_publish_autofix_enabled: false,
  beta_lp_enabled: false,
  beta_lp_variant_a_enabled: false,
  beta_lp_variant_b_enabled: false,
  founding_waitlist_enabled: false,
  founding_program_enabled: false,
} as const;

export type FeatureFlagKey = keyof typeof DEFAULT_FLAGS;
export type FeatureFlags = Record<FeatureFlagKey, boolean>;

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isLoading: boolean;
  isEnabled: (key: FeatureFlagKey) => boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

const CACHE_KEY = "adigo-feature-flags";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes

interface CachedFlags {
  flags: FeatureFlags;
  timestamp: number;
  version: number;
  /** Marker that these flags were actually fetched from DB, not defaults */
  fetched: boolean;
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    // Try to load from cache on init — only trust cache if flags were actually fetched
    if (typeof window !== "undefined") {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const parsed: CachedFlags = JSON.parse(cached);
          if (parsed.fetched && Date.now() - parsed.timestamp < CACHE_TTL) {
            return parsed.flags;
          }
        } catch {
          // Invalid cache, use defaults
        }
      }
    }
    return DEFAULT_FLAGS;
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    try {
      // Fetch flags for prod environment
      const { data: envData, error: envError } = await supabase
        .from("config_environments")
        .select("id, config_version")
        .eq("key", "prod")
        .maybeSingle();

      if (envError) {
        console.warn("[FeatureFlags] env fetch error:", envError.message);
        throw envError;
      }

      if (!envData) {
        console.warn("[FeatureFlags] No prod environment found");
        setIsLoading(false);
        return;
      }

      // Check if we have a valid cached version (must have been actually fetched, not defaults)
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const parsed: CachedFlags = JSON.parse(cached);
          if (parsed.fetched && parsed.version === envData.config_version && Date.now() - parsed.timestamp < CACHE_TTL) {
            setFlags(parsed.flags);
            setIsLoading(false);
            return;
          }
        } catch {
          // Continue to fetch
        }
      }

      // Fetch all flags for this environment
      const { data: items, error: itemsError } = await supabase
        .from("config_items")
        .select("key, default_value, enabled_by_default, kill_switch, kind")
        .eq("env_id", envData.id)
        .eq("kind", "flag");

      if (itemsError) {
        console.warn("[FeatureFlags] items fetch error:", itemsError.message);
        throw itemsError;
      }

      if (items && items.length > 0) {
        const newFlags: FeatureFlags = { ...DEFAULT_FLAGS };
        
        items.forEach((item) => {
          const key = item.key as FeatureFlagKey;
          if (key in DEFAULT_FLAGS) {
            // Kill switch forces flag to false
            if (item.kill_switch) {
              newFlags[key] = false;
            } else {
              newFlags[key] = Boolean(item.enabled_by_default || item.default_value === true);
            }
          }
        });

        setFlags(newFlags);

        // Cache the flags — mark as actually fetched from DB
        const cacheData: CachedFlags = {
          flags: newFlags,
          timestamp: Date.now(),
          version: envData.config_version,
          fetched: true,
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      } else {
        console.warn("[FeatureFlags] No flag items returned — possible RLS issue or empty table");
        // Don't cache empty results — try again next time
      }
    } catch (error) {
      console.error("Failed to fetch feature flags:", error);
      // Keep using current flags (from cache or defaults)
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Periodic refresh
  useEffect(() => {
    const interval = setInterval(fetchFlags, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchFlags]);

  // Refresh on window focus
  useEffect(() => {
    const handleFocus = () => {
      fetchFlags();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchFlags]);

  // Refresh when user changes (re-evaluate user-specific rules)
  useEffect(() => {
    if (user) {
      fetchFlags();
    }
  }, [user?.id, fetchFlags]);

  const isEnabled = useCallback((key: FeatureFlagKey): boolean => {
    return flags[key] ?? DEFAULT_FLAGS[key];
  }, [flags]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading, isEnabled, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");
  }
  return context;
}

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
}
